import { FEATURE_FLAGS } from "@/base/utils/feature-flags";
import { useRedirectIf } from "@/flokit/utils/routing/use-redirect-if";
import { LDFlagSet } from "launchdarkly-js-sdk-common";
import {
  useFlags,
  useLDClient,
  withLDConsumer,
} from "launchdarkly-react-client-sdk";
import { useFlagFromKeyQuery } from "modules/graphql.types";
import React, { useMemo } from "react";
import { flagType } from "../layouts/with-navbar-layout";

const kebabize = (str?: string) => {
  if (str === undefined) return null;

  return str.replace(
    /[A-Z]+(?![a-z])|[A-Z]/g,
    ($, ofs) => (ofs ? "-" : "") + $.toLowerCase(),
  );
};

export const useIsServerFlagOn = (
  flagName: flagType | undefined | "",
): {
  url?: string;
  on?: boolean;
  orgEnabled?: boolean;
  isLoading?: boolean;
} => {
  const flagUrlResult = useFlagFromKeyQuery(
    {
      key: kebabize(flagName),
    },
    {
      enabled: flagName !== undefined,
    },
  );

  const result = useMemo(() => {
    return flagUrlResult.data?.flagFromKey;
  }, [flagUrlResult.data?.flagFromKey]);

  return {
    url: result?.url ?? "",
    on: result?.on ?? false,
    orgEnabled: result?.orgEnabled ?? false,
    isLoading: flagUrlResult.isLoading,
  };
};

const LaunchDarklyConsumer: React.FC<{
  flags?: LDFlagSet;
  flagName?: string;
  children?: React.ReactNode;
}> = ({ flags, flagName, children }) => {
  if (!flags || !flagName || !children) {
    return null;
  }

  return flags[flagName] === false ? null : (
    <React.Fragment>{children}</React.Fragment>
  );
};

const LDWithConsumer = withLDConsumer()(LaunchDarklyConsumer);

export const withFeatureFlag = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  flagName: string,
) => {
  const WithLaunchDarkly = (props: P) => {
    return (
      <LDWithConsumer flagName={flagName}>
        <WrappedComponent {...(props as P)} />
      </LDWithConsumer>
    );
  };
  return WithLaunchDarkly;
};

/**
 * Use for on/off feature flag return values
 * @param flagName
 * @returns boolean value
 */
export const useFeatureFlag = (flagName: string) => {
  const client = useFlags();
  return !!client[flagName];
};

/**
 * Use for flags with multiple potential values
 * @param flagNames
 * @returns true if all flags are enabled
 */
export const useAllFeatureFlags = (flagNames: string[]) => {
  const client = useFlags();
  return flagNames.every((flagName) => !!client[flagName]);
};

/**
 * Use for flags with multiple potential values
 * @param flagNames
 * @returns true if any of the flags are enabled
 */
export const useAnyFeatureFlag = (flagNames: string[]) => {
  const client = useFlags();
  return flagNames.some((flagName) => !!client[flagName]);
};

/**
 * Use for flags with multiple potential values
 * @param flagName
 * @returns the flag value
 */
export const useFeatureFlagValue = (flagName: string) => {
  const client = useFlags();
  return client[flagName];
};

// To be used as follows:
// const Test = (props: { name: string }) => <div />;
// const Fallback = (props: { email: string }) => <div />;
// const Flagged = withFeatureFlagFallback(Test, Fallback, "test-flag");
// This means the dev needs to provide BOTH props for the fallback component
// and the wrapped component. This is a bit of a pain, but since we don't know
// if the feature flag is enabled or not until the component is rendered we
// can't know which props to pass to the wrapped component.
// In practice a dev can use connected components or context and keep these prop free.
export const withFeatureFlagFallback = <P extends object, Q extends object>(
  WrappedComponent: React.ComponentType<P>,
  FallbackComponent: React.ComponentType<Q>,
  flagName: string,
) => {
  type CombinedProps = P & Q;
  const WithLaunchDarklyFallback: React.FC<CombinedProps> = (
    props: CombinedProps,
  ) => {
    const flags = useFlags();
    return (
      <>
        {flags[flagName] === true ? (
          <WrappedComponent {...(props as P)} />
        ) : flags[flagName] === false ? (
          <FallbackComponent {...(props as Q)} />
        ) : null}
      </>
    );
  };
  return WithLaunchDarklyFallback;
};

export default LDWithConsumer;

type UseFeatureFlagRedirectArgs = {
  /**
   * The page to which to redirect.
   */
  href: string;
  /**
   * Redirect will take place if the value for this flag is falsy.
   */
  flagName: keyof typeof FEATURE_FLAGS;
  isExternal?: boolean;
};
type UseMultipleFeatureFlagsRedirectArgs = {
  /**
   * The page to which to redirect.
   */
  href: string;

  /**
   * Redirect will take place if *all* of these flags are falsy.
   */
  flagNames: Array<keyof typeof FEATURE_FLAGS>;

  isExternal?: boolean;
};
/**
 * Redirects if a given feature flag is off. Waits for the LaunchDarkly client to initialize.
 *
 * WARNING: This hook does not wait long enough for org targeting, because that requires a query to the backend, and then calls 'identify' on the LD client in a 'useEffect'. So when using with org targeting, it will redirect early if the client has not already been suitably initialized (e.g. when you refresh, you will get redirected without the code waiting long enough for the flag value).
 */
export function useFeatureFlagRedirect(args: UseFeatureFlagRedirectArgs) {
  const isClientInitialized = !!useLDClient();
  const flag = useFeatureFlag(args.flagName);

  useRedirectIf({
    href: args.href,
    condition: isClientInitialized && !flag,
    isExternal: args.isExternal,
  });
}

/**
 * Redirects if a given feature flag is off. Waits for the LaunchDarkly client to initialize.
 *
 * WARNING: This hook does not wait long enough for org targeting, because that requires a query to the backend, and then calls 'identify' on the LD client in a 'useEffect'. So when using with org targeting, it will redirect early if the client has not already been suitably initialized (e.g. when you refresh, you will get redirected without the code waiting long enough for the flag value).
 */
export function useMultipleFeatureFlagsRedirect(
  args: UseMultipleFeatureFlagsRedirectArgs,
) {
  const isClientInitialized = !!useLDClient();
  const flags = useFlags();

  const areAllFlagsOff = args.flagNames.every((flagName) => !flags[flagName]);

  useRedirectIf({
    href: args.href,
    condition: isClientInitialized && areAllFlagsOff,
    isExternal: args.isExternal,
  });
}
